import { useState, useEffect, useRef, FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getIndividuals, getIndividualByClientId } from '../services/individualsService';
import { Individual } from '../types/individual';
import { useDebouncedValue } from '../hooks/useDebounce';
import { usePrograms } from '../hooks/usePrograms';
import { CONTACT_TYPES } from '../constants/contactTypes';
import { apiPost } from '../services/api';
import { formatDate } from '../utils/dateUtils';
import '../styles/FormPages.css';

interface ServiceVisitForm {
  individualID: string;
  visitDate: string;
  programID: string;
  contactTypes: string[];
  durationMinutes: string;
  summaryNotes: string;
  nextFollowUpDate: string;
  isRedFlagged: boolean;
}


export function LogServiceVisitPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { programs } = usePrograms();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [clients, setClients] = useState<Individual[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const debouncedSearch = useDebouncedValue(searchTerm, 400);
  const preselectedClientRef = useRef<Individual | null>(null);
  const [formData, setFormData] = useState<ServiceVisitForm>({
    individualID: '',
    visitDate: new Date().toISOString().split('T')[0],
    programID: '',
    contactTypes: [],
    durationMinutes: '',
    summaryNotes: '',
    nextFollowUpDate: '',
    isRedFlagged: false,
  });

  // Initial load + pre-select from URL param
  useEffect(() => {
    const fetchInitial = async () => {
      try {
        const result = await getIndividuals({ isActive: true, pageSize: 100 });
        const clientsData = result.data || [];
        setClients(clientsData);

        // Pre-select client if provided in URL
        const clientIdParam = searchParams.get('clientId');
        if (clientIdParam) {
          let client = clientsData.find((c) => c.clientID === clientIdParam);
          if (!client) {
            try {
              const fetched = await getIndividualByClientId(clientIdParam);
              if (fetched && fetched.individualID) {
                client = fetched;
                preselectedClientRef.current = client;
                clientsData.push(client);
                setClients([...clientsData]);
              }
            } catch {
              // Client not found or inactive
            }
          }
          if (client && client.individualID) {
            setFormData((prev) => ({ ...prev, individualID: String(client.individualID) }));
          }
        }
      } catch {
        setError('Failed to load client list. Please refresh the page to try again.');
      }
    };

    fetchInitial();
  }, [searchParams]);

  // Server-side search when user types (skip initial mount — handled by fetchInitial)
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (!debouncedSearch) {
      // Reset to initial load when search is cleared
      const refetch = async () => {
        try {
          const result = await getIndividuals({ isActive: true, pageSize: 100 });
          const data = result.data || [];
          // Keep pre-selected client in the list if not already present
          if (preselectedClientRef.current) {
            const exists = data.find(
              (c) => c.individualID === preselectedClientRef.current?.individualID
            );
            if (!exists) {
              data.push(preselectedClientRef.current);
            }
          }
          setClients(data);
        } catch {
          // Keep existing list on error
        }
      };
      refetch();
      return;
    }

    const searchClients = async () => {
      setSearchLoading(true);
      try {
        const result = await getIndividuals({
          isActive: true,
          searchQuery: debouncedSearch,
          pageSize: 100,
        });
        setClients(result.data || []);
      } catch {
        // Keep existing list on error
      } finally {
        setSearchLoading(false);
      }
    };

    searchClients();
  }, [debouncedSearch]);

  const selectedClient = clients.find(
    (client) => client.individualID === parseInt(formData.individualID)
  );

  const handleContactTypeToggle = (type: string) => {
    setFormData((prev) => ({
      ...prev,
      contactTypes: prev.contactTypes.includes(type)
        ? prev.contactTypes.filter((t) => t !== type)
        : [...prev.contactTypes, type],
    }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.contactTypes.length === 0) {
      setError('Please select at least one contact type');
      return;
    }

    // M3: Guard parseInt() to prevent NaN from reaching the backend
    const individualID = parseInt(formData.individualID);
    const programID = parseInt(formData.programID);
    if (isNaN(individualID) || !formData.individualID) {
      setError('Please select a client');
      return;
    }
    if (isNaN(programID) || !formData.programID) {
      setError('Please select a program');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        individualID,
        contactDate: formData.visitDate,
        programID,
        contactTypes: formData.contactTypes,
        durationMinutes: parseInt(formData.durationMinutes) || 0,
        summaryNotes: formData.summaryNotes,
        nextFollowUpDate: formData.nextFollowUpDate || undefined,
        isRedFlagged: formData.isRedFlagged,
      };

      // Remove undefined fields
      Object.keys(payload).forEach((key) => {
        const typedKey = key as keyof typeof payload;
        if (payload[typedKey] === undefined) {
          delete payload[typedKey];
        }
      });

      const response = await apiPost('/contact-logs', payload);

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to log service visit');
      }

      navigate('/clients');
    } catch (err: unknown) {
      const serverMessage = err instanceof Error ? err.message : '';
      // Check if the error message already contains user-friendly guidance from the server
      if (serverMessage.includes('temporary') || serverMessage.includes('Reference:')) {
        setError(serverMessage);
      } else if (
        serverMessage.includes('Network error') ||
        serverMessage.includes('NETWORK_ERROR')
      ) {
        setError(
          'Could not connect to the server. Please check your internet connection and try again.'
        );
      } else {
        setError(
          'Unable to save the service visit. Please wait a moment and try again. ' +
            'If the problem continues, contact your supervisor.'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-page">
      <div className="page-header">
        <h2>Log Service Visit</h2>
        <p>Record client contact and service delivery</p>
      </div>

      {formData.isRedFlagged && (
        <div className="alert alert-warning">
          <strong>⚠ Red Flag:</strong> This contact has been marked as requiring immediate
          attention.
        </div>
      )}

      <form onSubmit={handleSubmit} className="intake-form">
        {error && <div className="alert alert-error">{error}</div>}

        <div className="form-section">
          <h3>Client and Visit Information</h3>

          <div className="form-group">
            <label htmlFor="search">Search for Client *</label>
            <input
              type="text"
              id="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, Client ID, phone, or date of birth..."
              className="search-input"
            />
          </div>

          {selectedClient && (
            <div className="selected-individual">
              <div className="selected-label">Selected Client:</div>
              <div className="individual-info">
                <strong>
                  {selectedClient.firstName} {selectedClient.lastName}
                </strong>
                <span className="client-id">{selectedClient.clientID}</span>
              </div>
            </div>
          )}

          <div
            className="individuals-list"
            style={{
              maxHeight: '300px',
              overflowY: 'auto',
              border: '1px solid #ddd',
              borderRadius: '4px',
              padding: '10px',
              marginBottom: '20px',
            }}
          >
            {searchLoading ? (
              <div className="empty-state">
                <p>Searching...</p>
              </div>
            ) : clients.length === 0 ? (
              <div className="empty-state">
                {searchTerm ? (
                  <p>No clients found matching &quot;{searchTerm}&quot;</p>
                ) : (
                  <p>
                    No clients available.{' '}
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => navigate('/clients/intake')}
                    >
                      Add a client first
                    </button>
                  </p>
                )}
              </div>
            ) : (
              clients.map((client) => (
                <label
                  key={client.individualID}
                  className="individual-option"
                  style={{
                    display: 'block',
                    padding: '10px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #eee',
                  }}
                >
                  <input
                    type="radio"
                    name="client"
                    value={client.individualID}
                    checked={formData.individualID === client.individualID?.toString()}
                    onChange={(e) => setFormData({ ...formData, individualID: e.target.value })}
                    style={{ marginRight: '10px' }}
                  />
                  <div style={{ display: 'inline-block' }}>
                    <div>
                      <strong>
                        {client.firstName} {client.lastName}
                      </strong>
                      <span style={{ marginLeft: '10px', color: '#666', fontSize: '0.9em' }}>
                        {client.clientID}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.9em', color: '#666' }}>
                      DOB: {formatDate(client.dateOfBirth)}
                      {client.phoneNumber && <span> • {client.phoneNumber}</span>}
                    </div>
                  </div>
                </label>
              ))
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="visitDate">Visit Date *</label>
              <input
                type="date"
                id="visitDate"
                value={formData.visitDate}
                onChange={(e) => setFormData({ ...formData, visitDate: e.target.value })}
                max={new Date().toISOString().split('T')[0]}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="durationMinutes">Duration (minutes) *</label>
              <input
                type="number"
                id="durationMinutes"
                value={formData.durationMinutes}
                onChange={(e) => setFormData({ ...formData, durationMinutes: e.target.value })}
                min="0"
                placeholder="30"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="programID">Program/Service *</label>
            <select
              id="programID"
              value={formData.programID}
              onChange={(e) => setFormData({ ...formData, programID: e.target.value })}
              required
            >
              <option value="">Select a program...</option>
              {programs.map((program) => (
                <option key={program.programID} value={String(program.programID)}>
                  {program.programName}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Contact Type(s) *</label>
            <div className="checkbox-group">
              {CONTACT_TYPES.map((type) => (
                <label key={type} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.contactTypes.includes(type)}
                    onChange={() => handleContactTypeToggle(type)}
                  />{' '}
                  {type}
                </label>
              ))}
            </div>
            {formData.contactTypes.length === 0 && error && (
              <small className="form-hint error">At least one contact type is required</small>
            )}
          </div>
        </div>

        <div className="form-section">
          <h3>Visit Details</h3>

          <div className="form-group">
            <label htmlFor="summaryNotes">Summary Notes *</label>
            <textarea
              id="summaryNotes"
              value={formData.summaryNotes}
              onChange={(e) => setFormData({ ...formData, summaryNotes: e.target.value })}
              rows={5}
              placeholder="Describe the purpose of the visit, services provided, and any observations..."
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="nextFollowUpDate">Next Follow-Up Date</label>
            <input
              type="date"
              id="nextFollowUpDate"
              value={formData.nextFollowUpDate}
              onChange={(e) => setFormData({ ...formData, nextFollowUpDate: e.target.value })}
              // TODO: Re-enable min date validation after backlog entries are complete
              // min={new Date().toISOString().split('T')[0]}
            />
            <small className="form-hint">Leave blank if no follow-up needed</small>
          </div>

          <div className="form-group">
            <label className="checkbox-label" style={{ display: 'block', marginTop: '1rem' }}>
              <input
                type="checkbox"
                checked={formData.isRedFlagged}
                onChange={(e) => setFormData({ ...formData, isRedFlagged: e.target.checked })}
              />{' '}
              <strong>Red Flag - Requires Immediate Attention</strong>
            </label>
            <small className="form-hint">
              Check this box if the client situation requires urgent intervention or supervisor
              notification
            </small>
          </div>
        </div>

        <div className="form-actions">
          <button type="button" className="button-secondary" onClick={() => navigate('/clients')}>
            Cancel
          </button>
          <button
            type="submit"
            className="button-primary"
            disabled={loading || clients.length === 0}
          >
            {loading ? 'Logging Visit...' : 'Log Service Visit'}
          </button>
        </div>
      </form>
    </div>
  );
}
